import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Kullanıcının gönderdiği talepleri getir
export const getUserRequests = createAsyncThunk(
  "requests/getUserRequests",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/requests/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Terapistin aldığı talepleri getir
export const getTherapistRequests = createAsyncThunk(
  "requests/getTherapistRequests",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/requests/therapist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Admin - Tüm talepleri getir
export const getAllRequests = createAsyncThunk(
  "requests/getAllRequests",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Yeni talep oluştur (güncellenmiş - availabilityIds ile)
export const createRequest = createAsyncThunk(
  "requests/createRequest",
  async (requestData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/requests`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Talep durumunu güncelle
export const updateRequest = createAsyncThunk(
  "requests/updateRequest",
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/requests/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Talep sil (Admin)
export const deleteRequest = createAsyncThunk(
  "requests/deleteRequest",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Talep ara (Admin)
export const searchRequests = createAsyncThunk(
  "requests/searchRequests",
  async (searchParams, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const queryString = new URLSearchParams(searchParams).toString();
      const response = await axios.get(`${API_URL}/requests/search?${queryString}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Kullanıcı dosya yükleme
export const uploadUserFile = createAsyncThunk(
  "requests/uploadUserFile",
  async ({ requestId, file }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API_URL}/requests/user-file/${requestId}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
      });
      return { requestId, filename: response.data.filename };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Terapist dosya yükleme
export const uploadTherapistFile = createAsyncThunk(
  "requests/uploadTherapistFile",
  async ({ requestId, file }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API_URL}/requests/therapist-file/${requestId}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
      });
      return { requestId, filename: response.data.filename };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Kullanıcı dosya silme
export const deleteUserFile = createAsyncThunk(
  "requests/deleteUserFile",
  async ({ requestId, fileName }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/requests/delete-user-file/${requestId}`, 
        { fileName }, 
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return { requestId, fileName };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Terapist dosya silme
export const deleteTherapistFile = createAsyncThunk(
  "requests/deleteTherapistFile",
  async ({ requestId, fileName }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/requests/delete-therapist-file/${requestId}`, 
        { fileName }, 
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return { requestId, fileName };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const requestSlice = createSlice({
  name: "requests",
  initialState: { 
    requests: [], 
    searchResults: {
      requests: [],
      countRequests: 0,
      page: 1,
      pages: 0
    },
    loading: false, 
    error: null,
    uploadLoading: false,
    deleteLoading: false
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRequests: (state) => {
      state.requests = [];
    },
    clearSearchResults: (state) => {
      state.searchResults = {
        requests: [],
        countRequests: 0,
        page: 1,
        pages: 0
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Get User Requests
      .addCase(getUserRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(getUserRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Therapist Requests
      .addCase(getTherapistRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTherapistRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(getTherapistRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get All Requests (Admin)
      .addCase(getAllRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(getAllRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Request
      .addCase(createRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.request) {
          state.requests.push(action.payload.request);
        }
      })
      .addCase(createRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Request
      .addCase(updateRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRequest.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(req => req._id === action.payload._id);
        if (index !== -1) {
          state.requests[index] = action.payload;
        }
      })
      .addCase(updateRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Request
      .addCase(deleteRequest.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteRequest.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.requests = state.requests.filter(req => req._id !== action.payload);
      })
      .addCase(deleteRequest.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })

      // Search Requests
      .addCase(searchRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Upload User File
      .addCase(uploadUserFile.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadUserFile.fulfilled, (state, action) => {
        state.uploadLoading = false;
        const { requestId, filename } = action.payload;
        const request = state.requests.find(req => req._id === requestId);
        if (request) {
          if (!request.userFile) request.userFile = [];
          request.userFile.push(filename);
        }
      })
      .addCase(uploadUserFile.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload;
      })

      // Upload Therapist File
      .addCase(uploadTherapistFile.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadTherapistFile.fulfilled, (state, action) => {
        state.uploadLoading = false;
        const { requestId, filename } = action.payload;
        const request = state.requests.find(req => req._id === requestId);
        if (request) {
          if (!request.therapistFile) request.therapistFile = [];
          request.therapistFile.push(filename);
        }
      })
      .addCase(uploadTherapistFile.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload;
      })

      // Delete User File
      .addCase(deleteUserFile.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteUserFile.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const { requestId, fileName } = action.payload;
        const request = state.requests.find(req => req._id === requestId);
        if (request && request.userFile) {
          request.userFile = request.userFile.filter(file => file !== fileName);
        }
      })
      .addCase(deleteUserFile.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })

      // Delete Therapist File
      .addCase(deleteTherapistFile.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteTherapistFile.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const { requestId, fileName } = action.payload;
        const request = state.requests.find(req => req._id === requestId);
        if (request && request.therapistFile) {
          request.therapistFile = request.therapistFile.filter(file => file !== fileName);
        }
      })
      .addCase(deleteTherapistFile.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearRequests, clearSearchResults } = requestSlice.actions;
export default requestSlice.reducer;